/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { createElement, type ReactElement, type ReactNode, useMemo, useState } from 'react'

import { useInterval } from '@utils/frontend'

import Cursor from './Cursor'

interface AIWriterProps {
	readonly children: React.ReactNode;
	readonly onFinish?: (() => void) | undefined;
	readonly onChange?: (() => void) | undefined;
	readonly delay?: number | undefined;
}

export default function AIWriter({ children, onFinish, onChange, delay }: AIWriterProps) {
	const [pos, setPos] = useState(0)

	if (delay === undefined) {
		delay = 50
	}

	const tokenLengths = useMemo(() => {
		const arr: number[] = []

		const traverseNodesAndCountTokens = (reactNode: string | React.ReactNode) => {
			const nodeChildren = (reactNode as ReactElement<any>)?.props?.children as ReactNode

			if (Array.isArray(reactNode)) {
				reactNode.forEach((node: ReactNode) => {
					if (typeof node === 'object') {
						traverseNodesAndCountTokens(node)
					} else if (typeof node === 'string') {
						arr.push(node.split(' ').length)
					}
				})
			}

			if (nodeChildren === undefined) {
				if (typeof reactNode === 'string') {
					arr.push(reactNode.split(' ').length)
				}
			}

			if (typeof nodeChildren === 'object') {
				traverseNodesAndCountTokens(nodeChildren as ReactNode)
			}

			if (typeof nodeChildren === 'string') {
				arr.push(nodeChildren.split(' ').length)
			}

			return arr
		}

		return traverseNodesAndCountTokens(children)
	}, [children])

	const totalTokens = useMemo(
		() => tokenLengths.reduce((acc, curr) => acc + curr, 0),
		[tokenLengths]
	)

	const nodex = useMemo(() => {
		let tmpCurrentLoopTokenPos = 0
		let tmpCurrentLoopNodePos = 0

		const traverseNodesAndInjectAIWriter = (reactNode: ReactNode): any => {
			if (tmpCurrentLoopTokenPos > pos) {
				return undefined
			}

			const nodeChildren = (reactNode as ReactElement<any>)?.props?.children as React.ReactNode

			if (Array.isArray(reactNode)) {
				const joinedNodes: string[] = []

				reactNode.forEach((node: ReactNode) => {
					if (typeof node === 'object') {
						joinedNodes.push(traverseNodesAndInjectAIWriter(node as React.ReactNode) as string)
					} else if (typeof node === 'string') {
						tmpCurrentLoopTokenPos += node.split(' ').length
						tmpCurrentLoopNodePos++
						const [nodeIndex, currentNodePos] = getCurrentNodeOverflow(tokenLengths, pos)

						if (nodeIndex < tmpCurrentLoopNodePos) {
							joinedNodes.push(node.slice(0, getStringPosition(node, ' ', currentNodePos)))
						}

						joinedNodes.push(node)
					}
				})

				return joinedNodes
			}

			if (nodeChildren === undefined) {
				if (typeof reactNode === 'string') {
					tmpCurrentLoopTokenPos += reactNode.split(' ').length
					tmpCurrentLoopNodePos++

					const [nodeIndex, currentNodePos] = getCurrentNodeOverflow(tokenLengths, pos)

					if (nodeIndex < tmpCurrentLoopNodePos) {
						return reactNode.slice(0, getStringPosition(reactNode, ' ', currentNodePos))
					}

					return reactNode
				}
			}

			if (typeof nodeChildren === 'object') {
				return createElement(
					(reactNode as ReactElement).type,
					(reactNode as ReactElement).props,
					traverseNodesAndInjectAIWriter(nodeChildren)
				)
			}

			if (typeof nodeChildren === 'string') {
				tmpCurrentLoopTokenPos += nodeChildren.split(' ').length
				tmpCurrentLoopNodePos++
				const [nodeIndex, currentNodePos] = getCurrentNodeOverflow(tokenLengths, pos)

				if (nodeIndex < tmpCurrentLoopNodePos) {
					return createElement(
						(reactNode as ReactElement).type,
						(reactNode as ReactElement).props,
						nodeChildren.slice(0, getStringPosition(nodeChildren, ' ', currentNodePos))
					)
				}

				return reactNode
			}

			return undefined
		}

		return traverseNodesAndInjectAIWriter(children)
	}, [children, pos, tokenLengths]) as ReactElement[]

	useInterval(
		() => {
			if (pos + 1 >= totalTokens) {
				if (onFinish) {
					onFinish()
				}

				setPos(pos + 1)
				delay = undefined
			} else {
				if (onChange) {
					onChange()
				}

				setPos(pos + 1)
			}
		},
		totalTokens > pos ? delay : undefined
	)

	return (
		<>
			{ nodex }
			<span className="w-3 h-3 inline-block blink mb-1">
				<Cursor />
			</span>
		</>
	)
}

function getStringPosition(string: string, subString: string, index: number): number {
	return string.split(subString, index).join(' ').length
}

function getCurrentNodeOverflow(arr: number[], pos: number) {
	let acc = 0
	let index = 0

	// eslint-disable-next-line @typescript-eslint/prefer-for-of
	for (let i = 0; i < arr.length; i++) {
		if (acc + arr[i] > pos) {
			break
		}

		index++

		acc += arr[i]
	}

	return [index, pos - acc]
}
